/**
 * BaremeFormPage
 * Création ou modification d'un barème ITS avec validation et aperçu de simulation
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ArrowLeft, Save, AlertCircle } from 'lucide-react';
import { configurationApi } from '../api/configuration.api';

interface TrancheFormule {
  ordre: number;
  seuilMin: string;
  seuilMax: string;
  taux: string;
  deductionFixe: string;
  libelle: string;
}

interface FormData {
  nom: string;
  description: string;
  dateDebut: string;
  dateFin: string;
  tranches: TrancheFormule[];
}

const DEFAULT_TRANCHE: Omit<TrancheFormule, 'ordre'> = {
  seuilMin: '0',
  seuilMax: '',
  taux: '0',
  deductionFixe: '0',
  libelle: '',
};

function calculerITS(baseImposable: number, tranches: TrancheFormule[]): { total: number; detail: Array<{ ordre: number; base: number; taux: number; montant: number }> } {
  const sorted = [...tranches].sort((a, b) => a.ordre - b.ordre);
  let total = 0;
  const detail: Array<{ ordre: number; base: number; taux: number; montant: number }> = [];

  for (const t of sorted) {
    const min = parseFloat(t.seuilMin) || 0;
    const max = t.seuilMax ? parseFloat(t.seuilMax) : Infinity;
    const taux = parseFloat(t.taux) / 100;
    const deductionFixe = parseFloat(t.deductionFixe) || 0;

    if (baseImposable <= min) break;
    const base = Math.min(baseImposable, max) - min;
    if (base <= 0) continue;

    const montant = Math.max(0, Math.round(base * taux) - deductionFixe);
    total += montant;
    detail.push({ ordre: t.ordre, base, taux, montant });
  }

  return { total: Math.max(0, total), detail };
}

function validerTranches(tranches: TrancheFormule[]): string[] {
  const errors: string[] = [];
  if (tranches.length === 0) {
    errors.push('Au moins une tranche est requise');
    return errors;
  }

  const sorted = [...tranches].sort((a, b) => a.ordre - b.ordre);
  const last = sorted[sorted.length - 1];
  if (last.seuilMax !== '') {
    errors.push('La dernière tranche doit avoir un seuil max vide (infini)');
  }

  for (let i = 0; i < sorted.length - 1; i++) {
    const curr = sorted[i];
    const next = sorted[i + 1];
    const currMax = parseFloat(curr.seuilMax);
    const nextMin = parseFloat(next.seuilMin);
    if (!curr.seuilMax) {
      errors.push(`La tranche ${curr.ordre} doit avoir un seuil max (sauf la dernière)`);
      continue;
    }
    if (nextMin < currMax) {
      errors.push(`Tranche ${next.ordre} : le seuil min (${nextMin}) doit être ≥ seuil max de la tranche précédente (${currMax})`);
    }
  }

  sorted.forEach(t => {
    const taux = parseFloat(t.taux);
    if (isNaN(taux) || taux < 0 || taux > 100) {
      errors.push(`Tranche ${t.ordre} : le taux doit être entre 0 et 100`);
    }
  });

  return errors;
}

export function BaremeFormPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id?: string }>();
  const modeEdition = !!id;

  const [form, setForm] = useState<FormData>({
    nom: '',
    description: '',
    dateDebut: new Date().toISOString().slice(0, 10),
    dateFin: '',
    tranches: [
      { ordre: 1, seuilMin: '0', seuilMax: '60000', taux: '0', deductionFixe: '0', libelle: 'Tranche exonérée' },
      { ordre: 2, seuilMin: '60000', seuilMax: '', taux: '10', deductionFixe: '0', libelle: 'Tranche imposable' },
    ],
  });
  const [formInitialise, setFormInitialise] = useState(!modeEdition);

  const [montantTest, setMontantTest] = useState('300000');
  const [errors, setErrors] = useState<string[]>([]);
  const [submitError, setSubmitError] = useState('');
  const [submitErrorCode, setSubmitErrorCode] = useState('');

  // Charger le barème existant en mode édition
  const { data: baremes = [] } = useQuery<any[]>({
    queryKey: ['baremes-its'],
    queryFn: configurationApi.obtenirITS,
    enabled: modeEdition,
  });

  useEffect(() => {
    if (!modeEdition || !baremes.length || formInitialise) return;
    const bareme = baremes.find((b: any) => b.idConfigITS === id);
    if (!bareme) return;
    setForm({
      nom: bareme.nom || '',
      description: bareme.description || '',
      dateDebut: bareme.dateDebut ? bareme.dateDebut.slice(0, 10) : '',
      dateFin: bareme.dateFin ? bareme.dateFin.slice(0, 10) : '',
      tranches: (bareme.tranches || []).map((t: any) => ({
        ordre: t.ordre,
        seuilMin: String(t.seuilMin ?? 0),
        seuilMax: t.seuilMax !== null && t.seuilMax !== undefined ? String(t.seuilMax) : '',
        taux: String(Number(t.taux) * 100),
        deductionFixe: String(t.deductionFixe ?? 0),
        libelle: t.libelle || '',
      })),
    });
    setFormInitialise(true);
  }, [baremes, id, modeEdition, formInitialise]);

  const simulationResult = useMemo(() => {
    const base = parseFloat(montantTest) || 0;
    if (base <= 0 || form.tranches.length === 0) return null;
    return calculerITS(base, form.tranches);
  }, [montantTest, form.tranches]);

  const mutationCreer = useMutation({
    mutationFn: (data: any) => configurationApi.creerITS(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baremes-its'] });
      navigate('/paie/configuration/baremes');
    },
    onError: (e: any) => {
      const code = e?.response?.data?.code || '';
      setSubmitErrorCode(code);
      setSubmitError(e?.response?.data?.message || 'Erreur lors de la création du barème');
    },
  });

  const mutationModifier = useMutation({
    mutationFn: (data: any) => configurationApi.modifierITS(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['baremes-its'] });
      navigate('/paie/configuration/baremes');
    },
    onError: (e: any) => {
      const code = e?.response?.data?.code || '';
      setSubmitErrorCode(code);
      setSubmitError(e?.response?.data?.message || 'Erreur lors de la modification du barème');
    },
  });

  const isPending = mutationCreer.isPending || mutationModifier.isPending;

  const handleSubmit = () => {
    const errs = validerTranches(form.tranches);
    if (!form.nom.trim()) errs.unshift('Le nom est requis');
    if (!form.dateDebut) errs.unshift('La date de début est requise');
    setErrors(errs);
    setSubmitError('');
    setSubmitErrorCode('');
    if (errs.length > 0) return;

    const payload = {
      nom: form.nom,
      description: form.description || undefined,
      dateDebut: form.dateDebut,
      dateFin: form.dateFin || undefined,
      tranches: form.tranches.map(t => ({
        ordre: t.ordre,
        seuilMin: parseFloat(t.seuilMin) || 0,
        seuilMax: t.seuilMax ? parseFloat(t.seuilMax) : null,
        taux: parseFloat(t.taux) / 100,
        deductionFixe: parseFloat(t.deductionFixe) || 0,
        libelle: t.libelle || `Tranche ${t.ordre}`,
      })),
    };

    if (modeEdition) {
      mutationModifier.mutate(payload);
    } else {
      mutationCreer.mutate(payload);
    }
  };

  const addTranche = () => {
    const nextOrdre = Math.max(0, ...form.tranches.map(t => t.ordre)) + 1;
    setForm(prev => ({
      ...prev,
      tranches: [...prev.tranches, { ...DEFAULT_TRANCHE, ordre: nextOrdre }],
    }));
  };

  const removeTranche = (ordre: number) => {
    setForm(prev => ({ ...prev, tranches: prev.tranches.filter(t => t.ordre !== ordre) }));
  };

  const updateTranche = (ordre: number, field: keyof TrancheFormule, value: string) => {
    setForm(prev => ({
      ...prev,
      tranches: prev.tranches.map(t => t.ordre === ordre ? { ...t, [field]: value } : t),
    }));
  };

  const tranchesSorted = [...form.tranches].sort((a, b) => a.ordre - b.ordre);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* En-tête */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/paie/configuration/baremes')}
          className="text-content-subtle hover:text-content transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-content">
            {modeEdition ? 'Modifier le barème ITS' : 'Nouveau barème ITS'}
          </h1>
          <p className="text-sm text-content-subtle mt-0.5">Définissez les tranches du barème progressif</p>
        </div>
      </div>

      {/* Erreurs de validation */}
      {errors.length > 0 && (
        <div className="rounded-lg bg-danger-light border border-danger p-4">
          <ul className="text-sm text-danger space-y-1 list-disc list-inside">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      {/* Erreur soumission */}
      {submitError && (
        <div className="rounded-lg bg-danger-light border border-danger p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-danger font-medium">{submitError}</p>
            {submitErrorCode === 'BAREME_DEJA_UTILISE' && (
              <p className="text-sm text-danger mt-1">
                <Link
                  to="/paie/configuration/baremes/nouveau"
                  className="underline hover:no-underline font-medium"
                >
                  Créer un nouveau barème
                </Link>
                {' '}avec une date de début ultérieure.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* ── Formulaire (3 colonnes) ── */}
        <div className="lg:col-span-3 space-y-5">
          {/* Informations générales */}
          <div className="bg-surface rounded-xl border border-border shadow-sm p-5 space-y-4">
            <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Informations générales</h2>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Nom *</label>
              <input
                type="text"
                value={form.nom}
                onChange={e => setForm(p => ({ ...p, nom: e.target.value }))}
                placeholder="ex: Barème ITS 2026"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Description</label>
              <input
                type="text"
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                placeholder="Description optionnelle"
                className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Date début *</label>
                <input
                  type="date"
                  value={form.dateDebut}
                  onChange={e => setForm(p => ({ ...p, dateDebut: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-content-muted mb-1">Date fin</label>
                <input
                  type="date"
                  value={form.dateFin}
                  onChange={e => setForm(p => ({ ...p, dateFin: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-surface text-content"
                />
              </div>
            </div>
          </div>

          {/* Tranches */}
          <div className="bg-surface rounded-xl border border-border shadow-sm p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Tranches</h2>
              <button
                type="button"
                onClick={addTranche}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-text hover:text-primary transition-colors"
              >
                <Plus className="w-4 h-4" />
                Ajouter une tranche
              </button>
            </div>

            {/* En-têtes colonnes */}
            <div className="grid grid-cols-[40px_1fr_1fr_80px_1fr_1fr_36px] gap-2 text-xs font-semibold text-content-subtle uppercase tracking-wide px-1">
              <span>#</span>
              <span>Seuil min</span>
              <span>Seuil max</span>
              <span>Taux %</span>
              <span>Déd. fixe</span>
              <span>Libellé</span>
              <span></span>
            </div>

            <div className="space-y-2">
              {tranchesSorted.map(t => (
                <div key={t.ordre} className="grid grid-cols-[40px_1fr_1fr_80px_1fr_1fr_36px] gap-2 items-center">
                  <span className="text-xs text-content-subtle font-mono text-center">{t.ordre}</span>
                  <input
                    type="number"
                    value={t.seuilMin}
                    onChange={e => updateTranche(t.ordre, 'seuilMin', e.target.value)}
                    className="px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary font-mono bg-surface text-content"
                    placeholder="0"
                  />
                  <input
                    type="number"
                    value={t.seuilMax}
                    onChange={e => updateTranche(t.ordre, 'seuilMax', e.target.value)}
                    className="px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary font-mono bg-surface text-content"
                    placeholder="∞"
                  />
                  <input
                    type="number"
                    value={t.taux}
                    onChange={e => updateTranche(t.ordre, 'taux', e.target.value)}
                    className="px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary font-mono bg-surface text-content"
                    min="0" max="100" step="0.5"
                  />
                  <input
                    type="number"
                    value={t.deductionFixe}
                    onChange={e => updateTranche(t.ordre, 'deductionFixe', e.target.value)}
                    className="px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary font-mono bg-surface text-content"
                    placeholder="0"
                  />
                  <input
                    type="text"
                    value={t.libelle}
                    onChange={e => updateTranche(t.ordre, 'libelle', e.target.value)}
                    className="px-2 py-1.5 text-xs border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary bg-surface text-content"
                    placeholder={`Tranche ${t.ordre}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeTranche(t.ordre)}
                    className="flex items-center justify-center w-8 h-8 text-danger hover:bg-danger-light rounded-lg transition-colors"
                    aria-label="Supprimer la tranche"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <p className="text-xs text-content-subtle">
              La dernière tranche doit avoir le champ "Seuil max" vide (infini).
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/paie/configuration/baremes')}
              className="px-4 py-2 text-sm font-medium text-content-muted bg-surface border border-border rounded-lg hover:bg-surface-raised transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isPending}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-content-inverted bg-primary rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors"
            >
              <Save className="w-4 h-4" />
              {isPending ? 'Enregistrement…' : (modeEdition ? 'Enregistrer les modifications' : 'Enregistrer')}
            </button>
          </div>
        </div>

        {/* ── Aperçu simulation (2 colonnes) ── */}
        <div className="lg:col-span-2">
          <div className="bg-surface rounded-xl border border-border shadow-sm p-5 space-y-4 sticky top-6">
            <h2 className="text-sm font-semibold text-content-muted uppercase tracking-wide">Aperçu calcul</h2>

            <div>
              <label className="block text-xs font-medium text-content-muted mb-1">Base imposable test</label>
              <div className="relative">
                <input
                  type="number"
                  value={montantTest}
                  onChange={e => setMontantTest(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-mono bg-surface text-content"
                  placeholder="ex: 300000"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-content-subtle">F CFA</span>
              </div>
            </div>

            {simulationResult && (
              <div className="space-y-3">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-surface-raised border-b border-border">
                      <th className="px-2 py-2 text-left text-content-subtle font-medium">Tranche</th>
                      <th className="px-2 py-2 text-right text-content-subtle font-medium">Base</th>
                      <th className="px-2 py-2 text-right text-content-subtle font-medium">Taux</th>
                      <th className="px-2 py-2 text-right text-content-subtle font-medium">Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulationResult.detail.map(d => (
                      <tr key={d.ordre} className="border-b border-border">
                        <td className="px-2 py-2 text-content-muted">#{d.ordre}</td>
                        <td className="px-2 py-2 text-right font-mono text-content-muted">{d.base.toLocaleString('fr-FR')}</td>
                        <td className="px-2 py-2 text-right text-content-subtle">{(d.taux * 100).toFixed(1)}%</td>
                        <td className="px-2 py-2 text-right font-mono font-semibold text-primary-text">{d.montant.toLocaleString('fr-FR')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="flex items-center justify-between bg-primary-light rounded-lg px-3 py-2">
                  <span className="text-sm font-semibold text-content">ITS total</span>
                  <span className="text-base font-bold text-primary-text font-mono">
                    {simulationResult.total.toLocaleString('fr-FR')} F
                  </span>
                </div>
              </div>
            )}

            {!simulationResult && (
              <p className="text-xs text-content-subtle italic">
                Entrez un montant pour voir la simulation.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
